// Inside the game: postcards from Chandigarh (every picture is a screenshot from inside the game), sliding past as you
// scroll on a laptop, a swipeable row on a phone. Then what you can do there, and the one nod to the city's famous
// traffic cameras: the ChallanCity mode that's in the works.
import { useRef } from 'react';
import { REDUCE, ease, span, useScene } from './scenes';
import { Wheel } from './Wheel';

const CARDS = [
  { img: '/site/shots/aerial.webp', place: 'Panjab University, from the air', note: 'Lift a drone anywhere: the campus, the sectors, the lake, the hills.', pin: '160014' },
  { img: '/site/shots/s17.webp', place: 'Sector 17 Plaza', note: 'Meet at the fountain. The shop blocks and arcades, where they really are.', pin: '160017' },
  { img: '/site/shots/sukhna.webp', place: 'Sukhna Lake', note: 'Paddle boats, the long bund, and the Shivaliks across the water.', pin: '160001' },
  { img: '/site/shots/underpass.webp', place: 'The Jan Marg underpass', note: 'Down the amphitheatre steps, past the murals, into the Rose Garden.', pin: '160016' },
  { img: '/site/shots/pu-sc.webp', place: 'The Student Centre', note: 'Up the spiral ramp of the round drum of 1963, to the roof.', pin: '160014' },
  { img: '/site/shots/pu-night.webp', place: 'After dark', note: 'The sun sets, the lamps come on. The city has a night shift.', pin: '160014' },
  { img: '/site/shots/gandhi.webp', place: 'A lotus in concrete', note: 'Pierre Jeanneret’s building in its pool, at Panjab University. Walk right inside.', pin: '160014' },
];

const FEATURES: [string, string, string][] = [
  ['Walk it', 'On foot, at 1:1. Every road, roundabout and tree where it really is.', 'M12 4a2 2 0 1 1 0 4a2 2 0 1 1 0-4zM10 9l-3 12M14 9l3 12M8 13h8'],
  ['Ride and drive', 'Cars and bikes on the real roads, at the real distances.', 'M5 16h14l-2-6H7zM7 19a1.5 1.5 0 1 0 0.01 0M17 19a1.5 1.5 0 1 0 0.01 0M3 16h18'],
  ['Fly', 'A drone from anywhere: the City Beautiful the way the birds see it.', 'M12 12l8-4M12 12l-8-4M12 12v6M6 6a2 2 0 1 0 0.01 0M18 6a2 2 0 1 0 0.01 0M10 18h4'],
  ['Together', 'Everyone in one Chandigarh. Meet at the Plaza, talk by text or voice, or make a private room.', 'M8 11a3 3 0 1 0 0.01 0M16 11a3 3 0 1 0 0.01 0M3 20c1-3 3-5 5-5s4 2 5 5M11 20c1-3 3-5 5-5s4 2 5 5'],
  ['Day and night', 'The sun goes down, the Shivaliks turn violet and the street lights come on.', 'M12 3v2M12 19v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M3 12h2M19 12h2M12 8a4 4 0 1 0 0.01 0'],
  ['Nothing to install', 'It runs in the browser, on a phone or a laptop. Touch controls on one, keys on the other.', 'M7 3h10v18H7zM11 18h2'],
];

export function Inside() {
  const sec = useRef<HTMLElement>(null);
  const track = useRef<HTMLDivElement>(null);
  useScene(
    sec,
    (p) => {
      const t = track.current;
      if (!t || REDUCE || matchMedia('(max-width: 760px), (pointer: coarse)').matches) return;
      const over = t.scrollWidth - t.parentElement!.clientWidth;
      const x = -ease.sine(span(p, 0.04, 0.96)) * Math.max(0, over);
      t.style.transform = `translate3d(${x.toFixed(1)}px, 0, 0)`;
      t.style.setProperty('--p', p.toFixed(3));
    },
    12,
  );
  return (
    <>
      <section className="pp" id="inside" ref={sec} aria-labelledby="pp-title">
        <div className="pp-stage">
          <header className="pp-head">
            <p className="gx-kicker mono">03 · Inside the game</p>
            <h2 id="pp-title">
              Postcards from a city
              <br />
              <em>you can walk into.</em>
            </h2>
            <p className="dim">Every picture here is a screenshot from inside the game.</p>
          </header>
          <div className="pp-rail">
            <div className="pp-track" ref={track}>
              {CARDS.map((c, i) => (
                <article key={c.img} className="pp-card" style={{ '--r': `${[-2.2, 1.6, -1.2, 2.4, -1.8, 1.1, -2.6][i]}deg`, '--i': i } as React.CSSProperties}>
                  <div className="ph">
                    <img src={c.img} alt={`${c.place}, inside the game`} loading="lazy" decoding="async" />
                  </div>
                  <div className="back">
                    <p className="hand">{c.note}</p>
                    <p className="to mono">
                      {c.place}
                      <br />
                      Chandigarh {c.pin}
                    </p>
                    <span className="stamp" aria-hidden>
                      <Wheel className="w" />
                      <b className="mono">GG</b>
                    </span>
                    <span className="pmark mono" aria-hidden>
                      GG BHARAT · CHD
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="gx-sec ft" aria-labelledby="ft-title">
        <header className="gx-head rv">
          <p className="gx-kicker mono">What you can do</p>
          <h2 id="ft-title">
            The whole city
            <br />
            <em>is the level.</em>
          </h2>
        </header>
        <ul className="ft-grid">
          {FEATURES.map(([t, d, icon], i) => (
            <li key={t} className="ft-item rv" style={{ '--d': `${(i % 3) * 0.07}s` } as React.CSSProperties}>
              <svg viewBox="0 0 24 24" aria-hidden>
                <path d={icon} />
              </svg>
              <h3>{t}</h3>
              <p>{d}</p>
            </li>
          ))}
          <li className="ft-item ft-challan rv" style={{ '--d': '0.1s' } as React.CSSProperties}>
            <div className="slip mono" aria-hidden>
              <b>e-CHALLAN</b>
              <span>Madhya Marg · red light</span>
              <span>Fine: ₹0 (it’s a game)</span>
            </div>
            <h3>Mind the cameras</h3>
            <p>Chandigarh drives under its famous traffic cameras. ChallanCity mode, with red lights, speed traps and pretend e-challans, is in the works.</p>
            <span className="soon mono">In the works</span>
          </li>
        </ul>
      </section>
    </>
  );
}
